import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  createChildOrganizationSchema,
  Organization,
} from '@tinct/core-api-models';
import {useCreateChildOrganization} from '@tinct/core-api';
import {handleApiError} from '@tinct/web-form';
import * as z from 'zod';

const formSchema = createChildOrganizationSchema.extend({
  copyMembers: z.boolean(),
});
export type CreateChildOrganizationFormValues = z.infer<typeof formSchema>;

type UseCreateChildOrganizationFormOptions = {
  agencyId: string;
  onSuccess?: (organization: Organization) => void;
};

export const useCreateChildOrganizationForm = ({
  agencyId,
  onSuccess,
}: UseCreateChildOrganizationFormOptions) => {
  const form = useForm<CreateChildOrganizationFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      domain: '',
      copyMembers: false,
    },
  });

  const createChildOrganization = useCreateChildOrganization({
    onSuccess: (organization) => onSuccess?.(organization),
    // Surfaces 429 (agency `workspace_count` exhausted) and 400 (5203, not an
    // agency) as the root error; field errors land on their inputs.
    onError: (error) => handleApiError(error, form),
  });

  const handleSubmit = form.handleSubmit(({copyMembers, domain, name}) => {
    createChildOrganization.mutate({
      agencyId,
      copyMembers,
      data: {
        name,
        // An empty domain means "not set" — the API rejects "".
        domain: domain ? domain : undefined,
      },
    });
  });

  return {
    form,
    handleSubmit,
    isLoading: createChildOrganization.isPending,
    isSuccess: createChildOrganization.isSuccess,
    reset: () => form.reset(),
  };
};
